import React, { useCallback, useEffect, useLayoutEffect, useMemo, useReducer } from 'react'
import { LinkSoftTableColHeaderCell } from '@/components/report-tables/core/colHeaderCell'
import { useLocalization } from '@/hooks/localization'
import { leftOrRight, handleWidth, minColumnWidth } from './utils'

function TableTopHeader ({ metadata, globalMetadata, headerRenderer, columnSizes, currentCol, tableHeight, tableWidth, onColumnSizeChanged }) {
  const style = useMemo(() => ({ maxWidth: tableWidth }), [tableWidth])
  if (!Array.isArray(metadata)) return null
  return (
    <div className='table-top-header' style={style}>
      <div className='table-column-sizing'>
        {columnSizes.map((width, colIndex) => <ColumnResizer key={colIndex} tableHeight={tableHeight} height={metadata.length * 25} left={columnSizes.filter((x, i) => i < colIndex).reduce((a, b) => a + b, 0)} width={width} colIndex={colIndex} onChange={onColumnSizeChanged} />)}
      </div>
      {metadata.map((x, i) => <TableHeaderRow key={i} index={i} tableRowMetadata={metadata[i]} globalMetadata={globalMetadata} headerRenderer={headerRenderer} columnSizes={columnSizes} currentCol={currentCol} />)}
    </div>
  )
}

const TableHeaderRow = React.memo(function TableHeaderRow ({ index, tableRowMetadata, globalMetadata, headerRenderer, columnSizes, currentCol }) {
  return (
    <div className='table-header-row table-row'>
      {
        columnSizes.map((width, colIndex) => {
          const targetMetadata = tableRowMetadata[colIndex]
          if (!targetMetadata) return null
          const isVisible = targetMetadata.xCoord === colIndex && targetMetadata.yCoord === index
          const isCurrentCol = targetMetadata.xCoord === currentCol && !!targetMetadata.topHeaderMostBottom
          return isVisible ? (
            <LinkSoftTableColHeaderCell
              key={colIndex}
              {...targetMetadata}
              {...globalMetadata}
              headerRenderer={headerRenderer}
              value={targetMetadata.value}
              columnSizes={columnSizes}
              isCurrentCol={isCurrentCol}
            />
          ) : null
        })
      }
    </div>
  )
})

const ColumnResizer = React.memo(function ColumnResizer ({ colIndex, left, width, height, tableHeight, onChange }) {
  const [state, dispatch] = useReducer(resizeReducer, resizeReducerInitState)
  const { rtl } = useLocalization()
  const { position, origPosition, initWidth } = state
  const newWidth = state.width
  const visualHandleStyle = useMemo(() => ({
    height: tableHeight
  }), [tableHeight])
  useLayoutEffect(() => {
    dispatch({ type: 'init', left, width, rtl })
  }, [left, rtl, width])
  useLayoutEffect(() => {
    if (width !== newWidth && newWidth >= minColumnWidth) {
      if (!initWidth) {
        setTimeout(() => {
          onChange({
            colIndex,
            width: newWidth
          })
        }, 0)
      }
    }
  }, [colIndex, initWidth, newWidth, onChange, width])
  const style = useMemo(() => ({
    ...leftOrRight(rtl, position),
    height: height
  }), [height, position, rtl])
  const onMouseDown = useCallback(({ screenX }) => {
    dispatch({ type: 'start', screenX })
  }, [])
  useEffect(() => {
    const onMouseMove = ({ screenX }) => {
      dispatch({ type: 'move', screenX })
    }
    const onMouseUp = ({ screenX }) => {
      dispatch({ type: 'stop', screenX })
    }

    window.document.addEventListener('mouseup', onMouseUp)
    window.document.addEventListener('mousemove', onMouseMove)
    return () => {
      window.document.removeEventListener('mouseup', onMouseUp)
      window.document.removeEventListener('mousemove', onMouseMove)
    }
  }, [])
  return (
    <div className='table-column-size-handle' style={style} onMouseDown={onMouseDown}>
      {origPosition >= 0 && <div className='table-column-handle-visual' style={visualHandleStyle} />}
    </div>
  )
})

const resizeReducerInitState = {
  initWidth: true,
  rtl: false,
  left: 0,
  width: 0,
  position: 0,
  origPosition: -1,
  startScreenX: -1
}

function resizeReducer (state, action) {
  const { position, origPosition, startScreenX, left, width } = state
  switch (action.type) {
    case 'init': {
      const { left, width, rtl } = action
      return { ...state, left, width, rtl, position: (left + width - handleWidth), initWidth: true }
    }
    case 'start': {
      if (state.origPosition < 0) {
        return { ...state, origPosition: position, startScreenX: action.screenX }
      }
      break
    }
    case 'move': {
      const { screenX } = action
      if (origPosition >= 0) {
        let newPosition = origPosition + applyRtl(state.rtl, screenX - startScreenX)
        if (newPosition - left < minColumnWidth) { // to avoid too small columns, 5px minimum
          newPosition = left + minColumnWidth
        }
        return { ...state, position: newPosition }
      }
      break
    }
    case 'stop': {
      if (origPosition >= 0) {
        const { screenX } = action
        const newWidth = Math.max(width + applyRtl(state.rtl, screenX - startScreenX), minColumnWidth)
        return { ...state, startScreenX: -1, origPosition: -1, width: newWidth, initWidth: false }
      }
      break
    }
    default:
      break
  }
  return state
}

function applyRtl (rtl, left) {
  return rtl ? -left : left
}

export {
  TableTopHeader
}



// WEBPACK FOOTER //
// ./src/components/report-tables/core/topHeader.js